import React, { useState } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import {
  Box,
  Card,
  Typography,
  MenuItem,
  Select,
  FormControl
} from '@mui/material'
import styled from '@emotion/styled'
import {
  useAdminMachineTime,
  useSingleDepartment
} from '../../hooks/dashboard'
import { useGetFullDepartments } from '../../hooks/departmentHooks'

const MainCard = styled(Card)(() => ({
  '.chart-title': {
    fontSize: 18,
    fontWeight: '600',
    padding: 7
  },
  '.parent-bar-chart': {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#F0F6FF',
    border: '1px solid #DEDEDE',
    width: '100%'
  },
  '.Overall': {
    fontSize: '16px',
    fontWeight: '600'
  },
  '.percentile': {
    fontSize: '18px',
    fontWeight: '700'
  }
}))

const MachineBarChart = (props) => {
  const {
    title,
    height,
    barChartData = true
    // seriesData = []
  } = props

  // State to manage dropdown selection
  const [selectedCategory, setSelectedCategory] = useState('')

  const handleDropdownChange = (event) => {
    setSelectedCategory(event.target.value)
  }
  // Machine time data
  const {
    data: machineTimeData
    // isFetching: isMachineTime
  } = useAdminMachineTime()
  const adminMachineTime = machineTimeData?.data
  // single data
  const { data: singleData } = useSingleDepartment(
    selectedCategory,
    !!selectedCategory
  )

  const adminMachineList = singleData?.data

  const rowsMachineTime = adminMachineTime?.map((data) => {
    return {
      id: data?._id,
      department: data?.department?.name ? data?.department?.name : '-',
      idleTime: data?.idleTime ? data?.idleTime : '-',
      machiningTime: data?.machiningTime ? data?.machiningTime : '-',
      name: data?.name ? data?.name : '-',
      setupTime: data?.setupTime ? data?.setupTime : '-',
      breakdownTime: data?.breakdownTime ? data?.breakdownTime : '-'
    }
  })
  const rowsMachineSingle = adminMachineList?.map((data) => {
    return {
      id: data?._id,
      department: data?.department?.name ? data?.department?.name : '-',
      idleTime: data?.idleTime ? data?.idleTime : '-',
      machiningTime: data?.machiningTime ? data?.machiningTime : '-',
      name: data?.name ? data?.name : '-',
      setupTime: data?.setupTime ? data?.setupTime : '-',
      breakdownTime: data?.breakdownTime ? data?.breakdownTime : '-'
    }
  })

  const { data: departmentData } = useGetFullDepartments()

  const machineData = departmentData?.data?.map((item) => {
    return {
      value: item?.id,
      label: item?.name
    }
  })

  const options = {
    chart: {
      type: 'column',
      height
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: selectedCategory
        ? rowsMachineSingle?.map((data) => data.name)
        : rowsMachineTime?.map((data) => data.name) || [], // Creates 20 "M1" categories
      title: {
        text: 'Machines'
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Time'
      },
      stackLabels: {
        enabled: false,
        style: {
          fontWeight: 'bold',
          color: 'gray'
        }
      }
    },
    credits: { enabled: false },
    exporting: {
      enabled: false // This disables the three dots menu
    },
    tooltip: {
      // headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        },
        pointWidth: 20
      }
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      symbolRadius: 0, // Makes the legend symbols square
      itemStyle: {
        fontSize: '12px',
        fontWeight: '500'
      }
    },

    series: [
      {
        name: 'Breakdown Time',
        data: selectedCategory
          ? rowsMachineSingle?.map((row) => parseFloat(row.breakdownTime) || 0)
          : rowsMachineTime?.map((row) => parseFloat(row.breakdownTime) || 0),
        color: '#FF0000'
      },
      {
        name: 'Idle Time',
        data: selectedCategory
          ? rowsMachineSingle?.map((row) => parseFloat(row.idleTime) || 0)
          : rowsMachineTime?.map((row) => parseFloat(row.idleTime) || 0),
        color: '#800080'
      },
      {
        name: 'Setup Time',
        data: selectedCategory
          ? rowsMachineSingle?.map((row) => parseFloat(row.setupTime) || 0)
          : rowsMachineTime?.map((row) => parseFloat(row.setupTime) || 0),
        color: '#FFC107'
      },
      {
        name: 'Machining Time',
        data: selectedCategory
          ? rowsMachineSingle?.map((row) => parseFloat(row.machiningTime) || 0)
          : rowsMachineTime?.map((row) => parseFloat(row.machiningTime) || 0),
        color: '#008000'
      }
    ]
    // series: seriesData
  }

  return (
    <div className="App">
      <MainCard
        variant="standerd"
        sx={{
          border: '1px solid #E6EAEF',
          padding: '15px 15px 0px 15px',
          borderRadius: '0px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '10px'
          }}
        >
          <Typography className="chart-title">{title}</Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            {/* <InputLabel id="chart-dropdown-label">Filter</InputLabel> */}
            <Select
              labelId="chart-dropdown-label"
              value={selectedCategory}
              onChange={handleDropdownChange}
              // label="Filter"
              placeholder="Select Department"
              displayEmpty
            >
              <MenuItem value="" disabled>
                 Departments
              </MenuItem>
              {machineData?.map((data, ind) => {
                return (
                  <MenuItem color="black" value={data?.value} key={ind}>
                    {data?.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>

        <Box
          className={`${
            barChartData
              ? 'bar-chart-padding-dash'
              : 'column-chart-padding-dash'
          }`}
        >
          <HighchartsReact highcharts={Highcharts} options={options} />
        </Box>
      </MainCard>
    </div>
  )
}
export default MachineBarChart
